import { c as _c } from "react/compiler-runtime";
import { ArrowRight } from "lucide-react";
import type { MouseEventHandler, PropsWithChildren } from "react";
import { isValidUrl } from "common/lib/isValidUrl";
import { CallToActionStyle } from "common/types/graphql";
import { Heading } from "common/components/Heading";
import clsx from "classnames";
import { Button } from "common/components/Button";
import { useHasBeenInViewport } from "common/hooks/interaction/useHasBeenInViewport";
import { useEffect } from "react";
import { CallToActionTrackingEventType, trackCallToActionEvent } from "modules/promos/lib/tracking";
import { trackCallToActionEngaged, trackCallToActionViewed } from "common/lib/tcEventCollector";
import { navigateWindowLocation } from "@theconversation/shared-libraries/windowLocation";
export interface CallToActionProps {
  href: string;
  title?: string;
  text: string;
  style: CallToActionStyle;
  analyticsId: string;
}
export const CallToAction = t0 => {
  "use memo";

  const $ = _c(31);
  const {
    href,
    title,
    text,
    style,
    analyticsId
  } = t0;
  const sanitizedHref = isValidUrl(href) ? href : undefined;
  const {
    setRef,
    hasBeenInViewport
  } = useHasBeenInViewport(0);
  let t1;
  let t2;
  if ($[0] !== analyticsId || $[1] !== hasBeenInViewport) {
    t1 = () => {
      if (analyticsId && analyticsId.length > 0 && hasBeenInViewport) {
        trackCallToActionEvent(CallToActionTrackingEventType.Impression, analyticsId);
        trackCallToActionViewed(analyticsId);
      }
    };
    t2 = [hasBeenInViewport, analyticsId];
    $[0] = analyticsId;
    $[1] = hasBeenInViewport;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
  let t3;
  if ($[4] !== analyticsId || $[5] !== href) {
    t3 = event => {
      if (analyticsId && analyticsId.length > 0) {
        trackCallToActionEvent(CallToActionTrackingEventType.Engagement, analyticsId);
        trackCallToActionEngaged(analyticsId);
        event.preventDefault();
        setTimeout(() => {
          navigateWindowLocation(href);
        }, 5);
      }
    };
    $[4] = analyticsId;
    $[5] = href;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const trackClick = t3;
  if (title) {
    const t4 = style === CallToActionStyle.Default;
    const t5 = style === CallToActionStyle.Donate;
    const t6 = style === CallToActionStyle.Newsletter;
    let t7;
    if ($[7] !== t4 || $[8] !== t5 || $[9] !== t6) {
      t7 = clsx("my-6 flex flex-col items-center gap-6 px-6 py-8 text-center", {
        "bg-gray-50 text-black": t4,
        "bg-red-500 text-white": t5,
        "bg-indigo-600 text-white": t6
      });
      $[7] = t4;
      $[8] = t5;
      $[9] = t6;
      $[10] = t7;
    } else {
      t7 = $[10];
    }
    let t8;
    if ($[11] !== title) {
      t8 = <Heading level={3} styledAsLevel={4}>{title}</Heading>;
      $[11] = title;
      $[12] = t8;
    } else {
      t8 = $[12];
    }
    const t9 = style !== CallToActionStyle.Default;
    let t10;
    if ($[13] !== href || $[14] !== t9 || $[15] !== text || $[16] !== trackClick) {
      t10 = <Button size="large" onDark={t9} primary={true} href={href} onClick={trackClick}>{text}</Button>;
      $[13] = href;
      $[14] = t9;
      $[15] = text;
      $[16] = trackClick;
      $[17] = t10;
    } else {
      t10 = $[17];
    }
    let t11;
    if ($[18] !== setRef || $[19] !== t10 || $[20] !== t7 || $[21] !== t8) {
      t11 = <div className={t7} ref={setRef}>{t8}{t10}</div>;
      $[18] = setRef;
      $[19] = t10;
      $[20] = t7;
      $[21] = t8;
      $[22] = t11;
    } else {
      t11 = $[22];
    }
    return t11;
  }
  let t4;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <ArrowRight className="inline size-5 flex-none" />;
    $[23] = t4;
  } else {
    t4 = $[23];
  }
  let t5;
  if ($[24] !== sanitizedHref || $[25] !== text || $[26] !== trackClick) {
    t5 = <a href={sanitizedHref} className="inline-flex gap-3 text-sm font-bold underline hover:text-indigo-600" onClick={trackClick}>{text} {t4}</a>;
    $[24] = sanitizedHref;
    $[25] = text;
    $[26] = trackClick;
    $[27] = t5;
  } else {
    t5 = $[27];
  }
  let t6;
  if ($[28] !== setRef || $[29] !== t5) {
    t6 = <div className="my-4" ref={setRef}>{t5}</div>;
    $[28] = setRef;
    $[29] = t5;
    $[30] = t6;
  } else {
    t6 = $[30];
  }
  return t6;
};
export type CallToActionComponent = typeof CallToAction;